/**
 * Agents — CUSTOM agents only (ADR-0071). A custom agent is a generic governed
 * strategy (allow-listed read/write tools, NEVER privileged) with its own
 * instructions + capabilities + LLM config, bound to a chat/voice Interface to drive
 * an app's assistant. Under the `ai` macro-area.
 *
 * It used to ALSO list the SYSTEM agents (Ask / Odino / Assistente app) as a config-
 * binding table. Both reasons are gone: `ask` + `app_assistant` are RETIRED (the Ask
 * surface with its plane; `app_assistant` because it was a default wearing an agent's
 * name), and **Odino's home is Impostazioni and nowhere else** (the owner's rule) —
 * its config binding lives in the Odino settings section. So a system agent has no
 * business here; every row on this page is one an operator MADE.
 *
 * Native `<select>` / checkboxes are intentional: this is the operator console.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Pencil, Plus, Trash2 } from "lucide-react";

import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip } from "@/components/ui/tooltip";
import {
  type AiAgent,
  type AiCustomAgentCreate,
  agentsApi,
  llmConfigsApi,
  mcpApi,
  toolsApi,
} from "@/lib/api-ai";

const inputCls = "rounded-md border border-border bg-background px-2 py-1.5 text-sm";
const btnCls =
  "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-muted disabled:opacity-50";

const KEY = ["ai", "agents"] as const;

/**
 * Operator-facing label per capability. The BACKEND owns which exist
 * (`safe_capabilities` on the list response) — this only translates them, and it must
 * cover the WHOLE vocabulary: an unmapped key falls back to its raw name, which is how
 * `kb_read` / `uploads` / `code_exec` leaked into both the grid AND the dialog's
 * checkboxes. When you add a capability to `hub.llm.agents._CAPABILITY_TOOLS`, add its
 * label here too. The fallback STAYS as the guard — a new one must never crash the page.
 */
const CAP_LABELS: Record<string, string> = {
  datasets: "Dataset di catalogo",
  app_read: "Lettura tabelle app",
  app_write: "Scrittura tabelle app",
  kb_read: "Ricerca nelle basi di conoscenza",
  uploads: "Lettura file caricati",
  code_exec: "Esecuzione codice",
  web_fetch: "Lettura pagine web",
};

function capLabel(cap: string): string {
  return CAP_LABELS[cap] ?? cap;
}

/** How many capability chips the grid shows before collapsing the rest behind "+N". */
const CAP_CHIPS_SHOWN = 2;

const chipCls = "rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground";

/**
 * The row's capabilities: the first {@link CAP_CHIPS_SHOWN}, then a "+N" that reveals the
 * rest on hover. An agent can hold the whole 7-capability vocabulary, and listing them all
 * wrapped the cell onto three lines and drowned the row.
 */
function CapabilityChips({ capabilities }: { capabilities: string[] }) {
  if (capabilities.length === 0) return <span className="text-xs text-muted-foreground">—</span>;

  const shown = capabilities.slice(0, CAP_CHIPS_SHOWN);
  const rest = capabilities.slice(CAP_CHIPS_SHOWN);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((cap) => (
        <span key={cap} className={chipCls}>
          {capLabel(cap)}
        </span>
      ))}
      {rest.length > 0 && (
        <Tooltip
          content={
            <ul className="space-y-0.5">
              {rest.map((cap) => (
                <li key={cap}>{capLabel(cap)}</li>
              ))}
            </ul>
          }
        >
          {/* A button, not a span: the tooltip must also open on keyboard FOCUS, or the
              hidden capabilities are unreachable without a mouse. */}
          <button type="button" className={cn(chipCls, "cursor-default")}>
            +{rest.length}
          </button>
        </Tooltip>
      )}
    </div>
  );
}

export function AgentsPage() {
  const { can } = usePermissions();
  const canWrite = can("ai", "write");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => agentsApi.list() });
  const configsQ = useQuery({
    queryKey: ["ai", "llm-configs"],
    queryFn: () => llmConfigsApi.list(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => agentsApi.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });

  const [editing, setEditing] = useState<AiAgent | null>(null);
  const [creating, setCreating] = useState(false);

  // System agents are NOT listed here (ADR-0071): `ask`/`app_assistant` are retired
  // and Odino is edited only in Impostazioni. The filter STAYS as the guard — if a new
  // system agent ever appears, it must not silently leak onto this page.
  const customAgents = (q.data?.agents ?? []).filter((a) => !a.is_system);
  const configs = configsQ.data?.configs ?? [];
  const safeCaps = q.data?.safe_capabilities ?? [];
  const defaultCaps = q.data?.default_capabilities ?? [];

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <Bot className="h-4 w-4 text-muted-foreground" /> Agenti
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Guidano le interfacce <strong>chat</strong> e <strong>voce</strong> delle app, ognuno
            con istruzioni, strumenti sicuri e configurazione LLM propri. Ogni interfaccia deve
            indicarne uno. <em>Odino</em> si configura in Impostazioni.
          </p>
        </div>
        {canWrite && (
          <button type="button" className={btnCls} onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Nuovo
          </button>
        )}
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}

      {customAgents.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nessun agente personalizzato.
        </p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="w-[34%] py-2 pr-4">Agente</th>
              <th className="w-[34%] py-2 pr-4">Capacità</th>
              <th className="whitespace-nowrap py-2 pr-4">Configurazione LLM</th>
              <th className="whitespace-nowrap py-2 pr-4">Stato</th>
              <th className="w-px py-2" />
            </tr>
          </thead>
          <tbody>
            {customAgents.map((a) => (
              <tr key={a.id} className="border-b border-border/60 align-top">
                {/* Name ONLY: the key + description are edit-time detail (they're in the
                    dialog), not what you scan a list for. */}
                <td className="py-2 pr-4 font-medium">{a.name}</td>
                <td className="py-2 pr-4">
                  <CapabilityChips capabilities={a.capabilities} />
                </td>
                <td className="whitespace-nowrap py-2 pr-4 text-xs">
                  {a.llm_config_id
                    ? a.effective_config_name
                    : `Eredita${a.effective_config_name ? ` (${a.effective_config_name})` : ""}`}
                </td>
                <td className="whitespace-nowrap py-2 pr-4">
                  <span className={a.enabled ? "text-success text-xs" : "text-muted-foreground text-xs"}>
                    {a.enabled ? "attivo" : "disattivato"}
                  </span>
                </td>
                <td className="py-2">
                  {canWrite && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="rounded p-1 hover:bg-muted"
                        title="Modifica"
                        onClick={() => setEditing(a)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1 text-destructive hover:bg-muted"
                        title="Elimina"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(`Eliminare l’agente “${a.name}”?`)) remove.mutate(a.id);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(creating || editing) && (
        <CustomAgentDialog
          agent={editing}
          configs={configs}
          safeCaps={safeCaps}
          defaultCaps={defaultCaps}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: KEY });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/** Slugify a name into a candidate key (mirrors the backend `_KEY_RE` shape). */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/^[^a-z]+/, "")
    .slice(0, 64);
}

function CustomAgentDialog({
  agent,
  configs,
  safeCaps,
  defaultCaps,
  onClose,
  onSaved,
}: {
  agent: AiAgent | null;
  configs: { id: string; name: string }[];
  safeCaps: string[];
  defaultCaps: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = agent !== null;
  const [name, setName] = useState(agent?.name ?? "");
  const [key, setKey] = useState(agent?.key ?? "");
  const [keyTouched, setKeyTouched] = useState(isEdit);
  const [description, setDescription] = useState(agent?.description ?? "");
  const [systemPrompt, setSystemPrompt] = useState(agent?.system_prompt ?? "");
  const [caps, setCaps] = useState<string[]>(agent?.capabilities ?? defaultCaps);
  const [toolIds, setToolIds] = useState<string[]>(agent?.tool_ids ?? []);
  const [mcpIds, setMcpIds] = useState<string[]>(agent?.mcp_ids ?? []);
  const [configId, setConfigId] = useState(agent?.llm_config_id ?? "");
  const [enabled, setEnabled] = useState(agent?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  // The custom-tool catalog to attach from (ADR-0033 Phase D); without ai/tools
  // access the picker is simply empty.
  const toolsQ = useQuery({
    queryKey: ["ai", "tools"],
    queryFn: () => toolsApi.list(),
    retry: false,
  });
  const availableTools = toolsQ.data?.tools ?? [];
  // The MCP-server catalog to attach from (ADR-0033 Phase E); without ai/mcp
  // access the picker is simply empty.
  const mcpQ = useQuery({
    queryKey: ["ai", "mcp-servers"],
    queryFn: () => mcpApi.list(),
    retry: false,
  });
  const availableMcp = mcpQ.data?.servers ?? [];

  const effectiveKey = isEdit ? agent!.key : keyTouched ? key : slugify(name);

  const mut = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return agentsApi.update(agent!.id, {
          llm_config_id: configId || null,
          enabled,
          name,
          description: description || null,
          system_prompt: systemPrompt || null,
          capabilities: caps,
          tool_ids: toolIds,
          mcp_ids: mcpIds,
        });
      }
      const body: AiCustomAgentCreate = {
        key: effectiveKey,
        name,
        description: description || null,
        system_prompt: systemPrompt || null,
        capabilities: caps,
        tool_ids: toolIds,
        mcp_ids: mcpIds,
        llm_config_id: configId || null,
        enabled,
      };
      return agentsApi.create(body);
    },
    onSuccess: onSaved,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Errore"),
  });

  function toggleCap(cap: string) {
    setCaps((c) => (c.includes(cap) ? c.filter((x) => x !== cap) : [...c, cap]));
  }
  function toggleTool(id: string) {
    setToolIds((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  }
  function toggleMcp(id: string) {
    setMcpIds((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            {isEdit ? "Modifica agente" : "Nuovo agente personalizzato"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-muted-foreground">Nome</span>
            <input
              className={`${inputCls} mt-1 w-full`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Assistente vendite"
            />
          </label>
          {!isEdit && (
            <label className="block">
              <span className="text-xs text-muted-foreground">Chiave (univoca, immutabile)</span>
              <input
                className={`${inputCls} mt-1 w-full font-mono`}
                value={effectiveKey}
                onChange={(e) => {
                  setKeyTouched(true);
                  setKey(e.target.value);
                }}
                placeholder="sales-bot"
              />
            </label>
          )}
          <label className="block">
            <span className="text-xs text-muted-foreground">Descrizione</span>
            <input
              className={`${inputCls} mt-1 w-full`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Istruzioni</span>
            <textarea
              className={`${inputCls} mt-1 h-24 w-full`}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="Rispondi solo su prodotti e listini. Sii conciso."
            />
          </label>
          <fieldset>
            <legend className="text-xs text-muted-foreground">Capacità</legend>
            <div className="mt-1 space-y-1">
              {safeCaps.map((cap) => (
                <label key={cap} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={caps.includes(cap)} onChange={() => toggleCap(cap)} />
                  {capLabel(cap)}
                </label>
              ))}
            </div>
          </fieldset>
          {availableTools.length > 0 && (
            <fieldset>
              <legend className="text-xs text-muted-foreground">Strumenti</legend>
              <div className="mt-1 space-y-1">
                {availableTools.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={toolIds.includes(t.id)}
                      onChange={() => toggleTool(t.id)}
                    />
                    {t.name}
                    <span className="font-mono text-[10px] text-muted-foreground">{t.key}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          {availableMcp.length > 0 && (
            <fieldset>
              <legend className="text-xs text-muted-foreground">Server MCP</legend>
              <div className="mt-1 space-y-1">
                {availableMcp.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={mcpIds.includes(s.id)}
                      onChange={() => toggleMcp(s.id)}
                    />
                    {s.name}
                    <span className="font-mono text-[10px] text-muted-foreground">{s.key}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <label className="block">
            <span className="text-xs text-muted-foreground">Configurazione LLM</span>
            <select
              className={`${inputCls} mt-1 w-full`}
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
            >
              <option value="">Eredita la predefinita</option>
              {configs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Attivo
          </label>
          </div>

          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        </DialogBody>

        <DialogFooter>
          <button type="button" className={btnCls} onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className={`${btnCls} bg-primary text-primary-foreground hover:bg-primary/90`}
            disabled={mut.isPending || !name.trim() || (!isEdit && !effectiveKey)}
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
          >
            {isEdit ? "Salva" : "Crea"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
